/**
 * useGeometry Hook
 *
 * Detects gsp:hasGeometry in entity properties, then fetches gsp:asWKT
 * from the geometry node using the public /describe endpoint.
 */

import { useState, useEffect } from 'react';
import { wktToGeoJSON } from 'betterknown';
import { describeEntity } from '@services/sparql.service';
import type { EntityProperty } from '@/types';

const GSP_NS = 'http://www.opengis.net/ont/geosparql#';
const HAS_GEOMETRY = GSP_NS + 'hasGeometry';
const AS_WKT = GSP_NS + 'asWKT';
const EPSG_IRI_RE = /<http:\/\/www\.opengis\.net\/def\/crs\/EPSG\//i;

export interface GeometryResult {
  wkt: string | null;
  loading: boolean;
  error: string | null;
}

/**
 * Returns true if the given WKT literal can be parsed by betterknown.
 * Most entities expose multiple asWKT variants (some 3D/ZM) that the parser
 * does not support; we try each candidate and pick the first that succeeds.
 */
function isParseableWkt(value: string): boolean {
  try {
    return wktToGeoJSON(value) != null;
  } catch {
    return false;
  }
}

/**
 * Extracts the WKT literal from entity properties.
 *
 * Flow:
 *  1. Find gsp:hasGeometry object (URI of the geometry node)
 *  2. Fetch that node via /describe
 *  3. Extract gsp:asWKT literal value
 */
export function useGeometry(properties: EntityProperty[]): GeometryResult {
  const [wkt, setWkt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const geometryProp = properties.find((p) => p.predicate === HAS_GEOMETRY);
    const geometryObj = geometryProp?.objects.find((o) => o.type === 'uri');

    if (!geometryObj) {
      setWkt(null);
      return;
    }

    const geometryNodeUri = geometryObj.value;

    let cancelled = false;
    setLoading(true);
    setError(null);

    describeEntity(geometryNodeUri)
      .then((geomProperties) => {
        if (cancelled) return;

        const wktProp = geomProperties.find((p) => p.predicate === AS_WKT);
        // Most entities expose multiple asWKT variants. Filter out literals with
        // an EPSG CRS IRI prefix (assume bare lon/lat = WGS84) and pick the first
        // candidate that the WKT parser can actually parse, since some 3D/ZM
        // variants are unsupported.
        const literals = (wktProp?.objects ?? []).filter((o) => o.type === 'literal');
        const nonEpsg = literals.filter((o) => !EPSG_IRI_RE.test(o.value));
        const wktObj = nonEpsg.find((o) => isParseableWkt(o.value)) ?? nonEpsg[0] ?? literals[0];

        setWkt(wktObj?.value ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[useGeometry] Failed to fetch geometry:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [properties]);

  return { wkt, loading, error };
}
